/**
 * API functions for managing fund calendars
 * This file contains functions to fetch, retrieve, and save fund calendars
 */
import axios from "axios";
import { FundCalendar } from "@/types";
import getServiceEndpoint from "./ServiceEndpoint";
import { buildClientHeader } from "./buildHeader";

/**
 * Fetches all fund calendars from the server
 * @returns Promise containing an array of FundCalendar objects, or empty array if the request fails
 */
export const getFundCalendars = async (): Promise<FundCalendar[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/FundCalendars`,
      buildClientHeader()
    );

    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve fund calendars", response.statusText);
      return [];
    }

    return response.data ?? [];
  } catch (error) {
    console.error("Error fetching fund calendars:", error);
  }
  return [];
};

/**
 * Retrieves a specific fund calendar by its ID
 * @param id The unique identifier of the fund calendar to retrieve
 * @returns Promise containing the requested FundCalendar object
 */
export const getFundCalendarById = async (
  id: number
): Promise<FundCalendar> => {
  const response = await axios.get(
    `${getServiceEndpoint()}/FundCalendars/${id}`,
    buildClientHeader()
  );
  return response.data;
};

/**
 * Saves a fund calendar to the server
 * Creates a new calendar if ID is not provided, updates existing one if ID is passed
 * @param calendar The FundCalendar object to be saved
 * @returns Promise resolving to true if save was successful, false otherwise
 */
export const saveFundCalendar = async (
  calendar: FundCalendar
): Promise<boolean> => {
  try {
    const response = await axios.post(
      `${getServiceEndpoint()}/FundCalendars`,
      JSON.stringify(calendar),
      buildClientHeader()
    );

    if (response.status !== 200) {
      console.error("Failed to save fund calendar", response.statusText);
      return false;
    }
  } catch (error) {
    console.error("Error saving fund calendar:", error);
    return false;
  }
  return true;
};

/**
 * Retrieves all fund calendars associated with a specific fund
 * @param fundId The ID of the fund to retrieve calendars for
 * @returns Promise containing an array of FundCalendar objects, or empty array if the request fails
 */
export const getFundCalendarsByFundId = async (
  fundId: number
): Promise<FundCalendar[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/FundCalendars?fundId=${fundId}`,
      buildClientHeader()
    );

    if (response.status !== 200 || response.data === undefined) {
      console.log(
        `Failed to retrieve fund calendars for fund ${fundId}`,
        response.statusText
      );
      return [];
    }

    return response.data ?? [];
  } catch (error) {
    console.error(`Error fetching fund calendars for fund ${fundId}:`, error);
  }
  return [];
};
